import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { getToken } from "../lib/auth";
import PageHeader from "../components/PageHeader";
import { useTenant } from "../lib/tenant";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Card, CardHeader, CardTitle, CardContent,
} from "../components/ui/card";
import { Loader2, Upload, CheckCircle2, Clock, XCircle, Building2, Phone, ArrowLeft, Crown, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate } from "../lib/format";

const PLAN_OPTIONS = [
  { period: "monthly", months: 1, label: "1 Bulan", popular: false },
  { period: "monthly", months: 3, label: "3 Bulan", popular: false },
  { period: "monthly", months: 6, label: "6 Bulan", popular: false },
  { period: "yearly", months: 1, label: "1 Tahun", popular: true, badge: "Hemat ~17%" },
];

function StatusPill({ status }) {
  const map = {
    pending: { icon: Clock, cls: "bg-[#D99338]/15 text-[#A66A1F] border-[#D99338]/30", text: "Menunggu Verifikasi" },
    approved: { icon: CheckCircle2, cls: "bg-[#1B5E3F]/12 text-[#1B5E3F] border-[#1B5E3F]/30", text: "Disetujui" },
    rejected: { icon: XCircle, cls: "bg-[#D14D4D]/12 text-[#D14D4D] border-[#D14D4D]/30", text: "Ditolak" },
  };
  const m = map[status] || map.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${m.cls}`}>
      <Icon className="h-3.5 w-3.5" />
      {m.text}
    </span>
  );
}

export default function Upgrade() {
  const navigate = useNavigate();
  const { user, isOwner } = useAuth();
  const { tenant, refresh } = useTenant();
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(PLAN_OPTIONS[0]);
  const [proofFile, setProofFile] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/platform/settings").then((r) => setSettings(r.data)),
      isOwner ? api.get("/upgrade/my").then((r) => setHistory(r.data)) : Promise.resolve(),
    ])
      .catch(() => toast.error("Gagal memuat info upgrade"))
      .finally(() => setLoading(false));
  }, [isOwner]);

  const amount = useMemo(() => {
    if (!settings) return 0;
    if (selected.period === "yearly") return settings.yearly_price_idr * selected.months;
    return settings.monthly_price_idr * selected.months;
  }, [settings, selected]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Tersalin ke clipboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isOwner) {
      toast.error("Hanya owner yang bisa mengajukan upgrade");
      return;
    }
    if (!proofFile) {
      toast.error("Mohon upload bukti transfer");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", proofFile);
      const params = new URLSearchParams({
        period: selected.period,
        months: String(selected.months),
        notes,
      }).toString();
      const token = getToken();
      const r = await fetch(`${API}/upgrade/request?${params}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || "Gagal mengirim");
      }
      toast.success("Bukti transfer terkirim. Tim kami akan verifikasi max 1×24 jam.");
      setProofFile(null);
      setNotes("");
      const list = await api.get("/upgrade/my");
      setHistory(list.data);
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Gagal mengirim";
      toast.error(typeof msg === "string" ? msg : "Gagal mengirim");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#D16B4D]" />
      </div>
    );
  }

  const isActive = tenant?.plan === "active" || tenant?.plan === "free";
  const daysLeft = tenant?.days_remaining ?? 0;
  const expired = tenant?.is_expired;

  return (
    <>
      <PageHeader
        title="Upgrade Akun"
        subtitle="Lanjutkan akses penuh ke semua fitur Island Stay Manager."
        actions={
          <Button
            variant="outline"
            data-testid="btn-back-dashboard"
            onClick={() => navigate("/dashboard")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Button>
        }
      />

      {/* Status banner */}
      <div
        data-testid="upgrade-status-banner"
        className={`rounded-2xl border p-5 flex items-center gap-4 ${
          isActive
            ? "border-[#1B5E3F]/30 bg-[#1B5E3F]/8"
            : expired
            ? "border-[#D14D4D]/40 bg-[#D14D4D]/8"
            : daysLeft <= 7
            ? "border-[#D99338]/40 bg-[#D99338]/8"
            : "border-[#EBE6DE] bg-white"
        }`}
      >
        <div className="h-12 w-12 rounded-xl bg-white flex items-center justify-center shadow-sm flex-shrink-0">
          {isActive ? <Crown className="h-6 w-6 text-[#1B5E3F]" /> : <Sparkles className="h-6 w-6 text-[#D16B4D]" />}
        </div>
        <div className="flex-1">
          <div className="font-heading text-lg font-semibold text-[#172E21]">
            {isActive
              ? `Plan ${tenant.plan === "free" ? "Free" : "Aktif"} — Berlaku hingga ${formatDate(tenant.trial_ends_at)}`
              : expired
              ? "Trial sudah berakhir"
              : `Trial — Tersisa ${daysLeft} hari`}
          </div>
          <div className="text-sm text-[#586D61] mt-0.5">
            {isActive
              ? "Terima kasih telah berlangganan. Lakukan perpanjangan kapan saja di bawah."
              : expired
              ? "Aplikasi dalam mode view-only. Lakukan upgrade untuk mengaktifkan kembali fitur write."
              : "Pilih paket di bawah untuk mengaktifkan akun selamanya tanpa kehilangan data."}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Plan selector & form */}
        <Card className="border-[#EBE6DE]">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-[#172E21]">Pilih Paket</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-3">
              {PLAN_OPTIONS.map((opt) => {
                const isSel = opt.period === selected.period && opt.months === selected.months;
                const price =
                  opt.period === "yearly"
                    ? settings.yearly_price_idr * opt.months
                    : settings.monthly_price_idr * opt.months;
                return (
                  <button
                    key={`${opt.period}-${opt.months}`}
                    type="button"
                    data-testid={`plan-option-${opt.period}-${opt.months}`}
                    onClick={() => setSelected(opt)}
                    className={`relative text-left rounded-xl border-2 p-4 transition-all ${
                      isSel
                        ? "border-[#D16B4D] bg-[#D16B4D]/8 shadow-md"
                        : "border-[#EBE6DE] hover:border-[#D16B4D]/50"
                    }`}
                  >
                    {opt.badge && (
                      <Badge className="absolute -top-2 right-3 bg-[#1B5E3F] text-white text-[10px]">
                        {opt.badge}
                      </Badge>
                    )}
                    <div className="text-xs uppercase tracking-wider font-bold text-[#586D61]">
                      {opt.period === "yearly" ? "Tahunan" : "Bulanan"}
                    </div>
                    <div className="font-heading text-xl font-semibold text-[#172E21] mt-1">
                      {opt.label}
                    </div>
                    <div className="font-mono text-base text-[#D16B4D] font-semibold mt-2">
                      {formatIDR(price)}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Total */}
            <div className="rounded-xl bg-[#082618] text-white p-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-white/60 font-bold">Total Transfer</div>
                <div className="font-heading text-2xl font-semibold mt-1" data-testid="upgrade-total-amount">
                  {formatIDR(amount)}
                </div>
              </div>
              <Crown className="h-10 w-10 text-[#D16B4D]/60" />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-[#172E21]">Bukti Transfer</Label>
                <label
                  className="mt-2 flex items-center justify-center gap-2 border-2 border-dashed border-[#D16B4D]/40 rounded-xl p-6 cursor-pointer hover:bg-[#D16B4D]/5 transition-colors"
                  data-testid="upgrade-proof-upload"
                >
                  <Upload className="h-5 w-5 text-[#D16B4D]" />
                  <span className="text-sm text-[#586D61]">
                    {proofFile ? proofFile.name : "Klik untuk upload bukti (JPG/PNG/PDF, max 5MB)"}
                  </span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    className="hidden"
                    onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
              <div>
                <Label htmlFor="notes" className="text-sm font-medium text-[#172E21]">
                  Catatan (opsional)
                </Label>
                <Textarea
                  id="notes"
                  data-testid="upgrade-notes"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Mis. tanggal & jam transfer"
                  className="mt-2 border-[#EBE6DE]"
                />
              </div>
              <Button
                type="submit"
                data-testid="btn-submit-upgrade"
                disabled={submitting || !proofFile || !isOwner}
                className="w-full bg-[#D16B4D] hover:bg-[#B85A3F] text-white gap-2 h-11"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Kirim Bukti Transfer
              </Button>
              {!isOwner && (
                <p className="text-xs text-[#D14D4D] text-center">
                  Hanya owner yang bisa mengajukan upgrade. Hubungi owner Anda.
                </p>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Bank info */}
        <Card className="border-[#EBE6DE] bg-[#FDFBF7]">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-[#172E21] flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#D16B4D]" />
              Info Transfer Bank
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              data-testid="bank-info-display"
              className="rounded-xl bg-white border border-[#EBE6DE] p-5 font-mono text-sm whitespace-pre-line text-[#172E21] leading-relaxed"
            >
              {settings.bank_info}
            </div>
            <Button
              type="button"
              variant="outline"
              data-testid="btn-copy-bank"
              onClick={() => handleCopy(settings.bank_info)}
              className="w-full gap-2 border-[#D16B4D]/30 text-[#D16B4D] hover:bg-[#D16B4D]/8"
            >
              <Copy className="h-4 w-4" />
              Salin Info Bank
            </Button>

            <div className="rounded-xl bg-[#1B5E3F]/8 border border-[#1B5E3F]/20 p-4">
              <div className="font-heading text-sm font-bold text-[#1B5E3F] mb-1 flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Butuh Bantuan?
              </div>
              <p className="text-xs text-[#586D61] mb-2">
                Hubungi tim kami via WhatsApp untuk bantuan atau pertanyaan upgrade.
              </p>
              <a
                href={`https://wa.me/${settings.contact_whatsapp}?text=${encodeURIComponent(
                  `Halo, saya ingin upgrade akun ${tenant?.name || ""} (${user?.email || ""})`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-whatsapp-help"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#1B5E3F] hover:underline"
              >
                Chat WhatsApp →
              </a>
            </div>

            <div className="rounded-xl bg-[#D16B4D]/8 border border-[#D16B4D]/20 p-4 text-xs text-[#586D61] space-y-1.5">
              <div className="font-heading text-sm font-bold text-[#172E21]">Cara Kerja</div>
              <div>1. Pilih paket di sebelah kiri</div>
              <div>2. Transfer sesuai nominal ke rekening di atas</div>
              <div>3. Upload bukti transfer pada form</div>
              <div>4. Tim kami akan verifikasi maksimal 1×24 jam</div>
              <div>5. Akun otomatis aktif setelah disetujui</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* History */}
      {isOwner && history.length > 0 && (
        <Card className="border-[#EBE6DE]" data-testid="upgrade-history">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-[#172E21]">Riwayat Pengajuan</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {history.map((h) => (
                <div
                  key={h.id}
                  data-testid={`upgrade-history-${h.id}`}
                  className="flex items-center justify-between gap-4 p-4 rounded-xl border border-[#EBE6DE] bg-white"
                >
                  <div className="flex-1">
                    <div className="font-heading text-sm font-semibold text-[#172E21]">
                      {h.period === "yearly" ? `${h.months} Tahun` : `${h.months} Bulan`} • {formatIDR(h.amount)}
                    </div>
                    <div className="text-xs text-[#586D61] mt-1">
                      Diajukan {formatDate(h.created_at)}
                      {h.reviewed_at && ` • Diproses ${formatDate(h.reviewed_at)}`}
                    </div>
                    {h.admin_notes && (
                      <div className="text-xs text-[#D14D4D] mt-1">Catatan admin: {h.admin_notes}</div>
                    )}
                  </div>
                  <StatusPill status={h.status} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
